/**
 * P4-5: puts the section payload's copy of the open step into the Step repo
 * before StepView mounts, so the step kind and the filtered drill type are
 * known at once: SectionStepView picks StepView or StepTheoryView without a
 * swap, and a filter step's filter GET runs in parallel with the step GET.
 *
 * Only fields the section payload and `GET steps/:id` both carry are seeded,
 * with the same values; `step_type` keeps just the kind (`id`, `__component`),
 * so useStepData still sees no content and always runs the full GET, whose
 * save then fills in the rest. A step already in the repo is never touched.
 */
import { watch, type Ref } from 'vue';
import { useRepo } from 'pinia-orm';
import { Step } from 'src/modules/Step/models/Step';
import { useStepData } from 'src/modules/Step/useStepData';

type SectionPayloadStep = Partial<Step> | null | undefined;

const SHARED_FIELDS = [
  'step_name',
  'instructions',
  'step_category',
  'function',
  'type',
] as const;

/** The section payload's step reduced to what the step GET stores the same way. */
export function partialStepFromSection(
  step: SectionPayloadStep,
): Partial<Step> | null {
  const kind = step?.step_type?.[0];
  if (!step?.id || !step.documentId || !kind?.__component) return null;
  const partial: Record<string, unknown> = {
    id: step.id,
    documentId: step.documentId,
    step_type: [{ id: kind.id, __component: kind.__component }],
  };
  for (const key of SHARED_FIELDS) {
    if (step[key] != null) partial[key] = step[key];
  }
  return partial;
}

/** Seeds the repo unless it already holds the step. True when it seeded. */
export function seedStepFromSection(step: SectionPayloadStep): boolean {
  const partial = partialStepFromSection(step);
  if (!partial) return false;
  const repo = useRepo(Step);
  if (repo.where('documentId', partial.documentId).first()) return false;
  repo.save(partial as Step);
  return true;
}

export function useSectionStepSeed({
  stepDocId,
  sectionSteps,
}: {
  stepDocId: string | undefined;
  sectionSteps: Readonly<Ref<SectionPayloadStep[]>>;
}) {
  // Pre-flush: runs before SectionStepView renders the section that arrived.
  watch(
    () => sectionSteps.value.find((s) => s?.documentId === stepDocId),
    (step) => {
      // useStepData re-reads its shared documentStepRepo from the repo.
      if (stepDocId && seedStepFromSection(step)) useStepData(stepDocId);
    },
    { immediate: true },
  );
}
